import type { FilterSpec } from "@/components/data";

import { formatCount, variantLabel } from "./format";
import { DOORS } from "./stats-api";
import type {
  FunnelOutcomesStats,
  OutcomeCut,
  OutcomeRow,
  PageFigures,
  SourceOrders,
  VariantOrders,
} from "./stats-api";

const MONEY = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });

/** A figure the service did not send, or could not compute, is a dash, never £0.00. */
export function formatMoney(value: number | null | undefined): string {
  return value == null ? "–" : MONEY.format(value);
}

/** The service sends a rate as a fraction; a `null` rate (denominator 0) is a dash, never 0%. */
export function formatRate(value: number | null | undefined): string {
  return value == null ? "–" : `${(value * 100).toFixed(1)}%`;
}

export function formatOrders(value: number | null | undefined): string {
  return value == null ? "–" : formatCount(value);
}

/**
 * What each traffic source means, in the lander's own rules (transparency
 * `src/lib/landing/visits.ts` resolveSource): `utm_source` on the link wins; no tag
 * but a Ketosource storefront referrer is `banner`; anything else is `direct`.
 * Traffic from before source tracking began (25 Sep 2026) is `unknown`.
 */
const SOURCES: Record<string, { label: string; description: string }> = {
  newsletter: {
    label: "newsletter",
    description: "The link carried utm_source=newsletter: our Drip emails.",
  },
  drip: { label: "drip", description: "The link carried utm_source=drip: a Drip email or automation." },
  qr: { label: "qr", description: "A scanned Ketosource QR code (utm_source=qr is added to every tagged code)." },
  banner: {
    label: "banner",
    description: "No source tag, but the visitor came from a page on our own store, such as the site banner.",
  },
  direct: {
    label: "direct",
    description:
      "No source tag and no Ketosource referrer: a typed or pasted address, an app that hides where the click came from (WhatsApp, some email apps), or a link shared without a tag.",
  },
  unknown: {
    label: "Before tracking (pre 25 Sep)",
    description: "Visits and sign-ups from before source tracking started on 25 Sep 2026, so their source was never recorded.",
  },
  pack: {
    label: "pack",
    description: "An in-app checkout from someone who opened the app from the QR code on a pack (the app's src=pack).",
  },
  "pack-uk": {
    label: "pack-uk",
    description: "An in-app checkout from the QR code on a UK pack (the app's src=pack-uk).",
  },
  other: {
    label: "other",
    description: "The link had a utm_source the lander could not store (spaces or symbols in it).",
  },
};

const CUSTOM_SOURCE =
  "Custom link tag: recorded exactly as written in the link's utm_source. A random string is usually a bot, a link scanner or a test.";

/** A missing source is pre-tracking traffic, the same bucket the lander calls "unknown". Also the chip's URL value. */
export function sourceValue(source: string | null): string {
  return source === null || source === "" ? "unknown" : source;
}
const sourceKey = sourceValue;

export function sourceLabel(source: string | null): string {
  return SOURCES[sourceKey(source)]?.label ?? sourceKey(source);
}

/** The hover text for a source: what it means and how a visit gets it. */
export function sourceDescription(source: string | null): string {
  return SOURCES[sourceKey(source)]?.description ?? CUSTOM_SOURCE;
}


/**
 * The "Source · visitors" chip row: All, then every source with activity, each with
 * its hover description. A source chosen in the URL stays a chip even with no
 * traffic in this window, so the filter can always be seen and cleared.
 */
export function sourceFilterSpec(name: string, rows: SourceOrders[], chosen: string | null): FilterSpec {
  // C8 folds a missing source into "unknown" server-side; this only guards against a second chip with one value.
  const sorted = sortedSources(rows).filter(
    (row, index, all) => all.findIndex((other) => sourceValue(other.source) === sourceValue(row.source)) === index,
  );
  const missing = chosen !== null && !sorted.some((row) => sourceValue(row.source) === chosen);
  const option = (source: string | null, visitors: number) => ({
    value: sourceValue(source),
    label: `${sourceLabel(source)} ${formatCount(visitors)}`,
    description: sourceDescription(source),
  });
  return {
    name,
    label: "Source · visitors",
    kind: "choice",
    options: [
      { value: "", label: "All" },
      ...sorted.map((row) => option(row.source, row.visitors)),
      ...(missing ? [option(chosen, 0)] : []),
    ],
  };
}

/** URL value -> the services' `source` parameter: `null` = every source. */
export function sourceParam(value: unknown): string | null {
  if (typeof value !== "string" || value === "") return null;
  // `_none` was the pre-tracking chip for a few hours on 2026-10-07; old links still land right.
  return value === "_none" ? "unknown" : value;
}

/** A source the lander Worker accepts as a filter (its `SOURCE_PATTERN`). */
export const WORKER_SOURCE = /^[a-z0-9_-]{1,32}$/;

/** URL value -> a `locale` parameter: two lowercase letters, else `null` (every language). */
export function localeParam(value: unknown): string | null {
  return typeof value === "string" && /^[a-z]{2}$/.test(value) ? value : null;
}

/** The service's row for one page, or `undefined` when it has none (no activity in the window). */
export function figuresForPage(
  rows: PageFigures[] | undefined,
  page: { locale: string; variant: string },
): PageFigures | undefined {
  return rows?.find((row) => row.locale === page.locale && row.variant === page.variant);
}

/** Orders whose sign-up named no page, as the service sent them; `null` when there are none. */
export function unmatchedPagesLine(unmatched: { orders: number; revenue_gbp: number } | undefined): string | null {
  if (!unmatched || unmatched.orders === 0) return null;
  const noun = unmatched.orders === 1 ? "order" : "orders";
  return `${formatCount(unmatched.orders)} ${noun} (${formatMoney(unmatched.revenue_gbp)}) in this funnel could not be matched to a language page.`;
}

export const PAGE_ORDERS_CAPTION = "Orders are matched to the page a person signed up on, once a day.";

/** Highest revenue first, then most visitors. Order only: no value changes. */
export function sortedSources(rows: SourceOrders[]): SourceOrders[] {
  return [...rows].sort(
    (a, b) =>
      b.revenue_gbp - a.revenue_gbp ||
      b.visitors - a.visitors ||
      (b.app_revenue_gbp ?? 0) - (a.app_revenue_gbp ?? 0),
  );
}

/** The row for one version, or `undefined` when the service has none for it. */
export function ordersFor(rows: VariantOrders[] | undefined, variant: string): VariantOrders | undefined {
  return rows?.find((row) => row.variant === variant);
}

/** Orders whose sign-up recorded no version, as the service sent them; `null` when there are none. */
export function noVersionLine(rows: VariantOrders[] | undefined): string | null {
  const row = rows?.find((r) => r.variant === null);
  if (!row || row.orders === 0) return null;
  const noun = row.orders === 1 ? "order" : "orders";
  return `${formatCount(row.orders)} ${noun} (${formatMoney(row.revenue_gbp)}) came from sign-ups with no version recorded.`;
}

export function unattributedLine(unattributed: { orders: number; revenue_gbp: number } | undefined): string | null {
  if (!unattributed || unattributed.orders === 0) return null;
  const noun = unattributed.orders === 1 ? "order" : "orders";
  return `${formatCount(unattributed.orders)} ${noun} (${formatMoney(unattributed.revenue_gbp)}) in this window came from no funnel.`;
}

export const ORDERS_CAPTION = "Orders are matched to the version a person signed up on, once a day.";

/** A row's label on the Outcomes tab. A `null` key is named for what it means on that cut. */
export function outcomeLabel(by: OutcomeCut, row: OutcomeRow): string {
  const value = row[by] ?? null;
  if (by === "variant") return value === null || value === "" ? "No version recorded" : variantLabel(value);
  // Outcomes keep a missing first source as missing: there it is not only pre-tracking traffic.
  if (by === "source") return value === null || value === "" ? "No source recorded" : sourceLabel(value);
  return value === null || value === "" ? "Not from the app" : value;
}

export const SUPPRESSED_TEXT = "Fewer than 5 people";

/** Shown rows first, highest LTV then most customers; suppressed rows last. Order only: no value changes. */
export function sortedOutcomes(rows: OutcomeRow[]): OutcomeRow[] {
  return [...rows].sort(
    (a, b) =>
      Number(a.suppressed) - Number(b.suppressed) ||
      (b.ltv_gbp ?? -1) - (a.ltv_gbp ?? -1) ||
      (b.customers ?? 0) - (a.customers ?? 0),
  );
}

/** The cohort's totals in one sentence, every figure as the service sent it. */
export function outcomesSummaryLine(stats: FunnelOutcomesStats, name: string): string {
  const t = stats.totals;
  const people = t.customers === 1 ? "customer" : "customers";
  const base = `${name}, first orders in the last ${stats.window.days} days: ${formatOrders(t.customers)} ${people} · LTV ${formatMoney(t.ltv_gbp)} · contribution ${formatMoney(t.contribution_per_customer)} per customer · repeat rate ${formatRate(t.repeat_rate)}`;
  if (!t.suppressed_rows) return base;
  const groups = t.suppressed_rows === 1 ? "group" : "groups";
  return `${base} · ${formatCount(t.suppressed_rows)} ${groups} hidden to protect privacy`;
}

export const OUTCOMES_CAPTION =
  "Each figure is lifetime value to date for people whose first order fell in this window, credited to the page version or source they first came from. Compare versions on 90-day LTV: it only counts people at least 90 days past their first order.";

/** An app door as the Onboarding tab names it; a door the app did not record reads "Unknown door". */
export function doorLabel(door: string): string {
  return DOORS.find((d) => d.value === door && d.value !== "all")?.label ?? (door === "unknown" ? "Unknown door" : door);
}
